import Money from '@/components/text/money';
import { Tooltip } from '@/components/ui/tooltip';
import { useDownloadPayoutStatementAction } from '@/hooks/actions/use-download-payout-statement-action';
import type { InvolvedAffiliate, Transfer } from '@/types/payout';
import { Box, Button, Flex, IconButton, Table } from '@chakra-ui/react';
import { PayoutTransferStatus } from '@fluenceur/types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AffiliateInfo from '../affiliate/AffiliateInfo';
import TransferStatusCell from './TransferStatusCell';
import { Download } from 'iconoir-react';

interface PayoutTransfersTableProps {
  payoutId: string;
  transfers: Transfer[];
  involvedAffiliates: InvolvedAffiliate[];
}

const MAX_TRANSFERS_DISPLAY = 5;

const PayoutTransfersTable: React.FC<PayoutTransfersTableProps> = ({
  payoutId,
  transfers,
  involvedAffiliates,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { downloadStatement, downloadingTransferId } = useDownloadPayoutStatementAction();

  // Helper to get affiliate info
  const getAffiliateInfo = (affiliateId: string): { affiliate: InvolvedAffiliate | undefined } => {
    const affiliate = involvedAffiliates.find((ia) => ia.id === affiliateId);
    return { affiliate };
  };

  return (
    <Box position="relative">
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>{t('payouts.transfers.table.affiliate')}</Table.ColumnHeader>
            <Table.ColumnHeader>{t('payouts.transfers.table.amount')}</Table.ColumnHeader>
            <Table.ColumnHeader>{t('payouts.transfers.table.status')}</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="end">
              {t('payouts.transfers.table.statement')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(showAll ? transfers : transfers.slice(0, MAX_TRANSFERS_DISPLAY)).map((transfer) => {
            const { affiliate } = getAffiliateInfo(transfer.affiliateId);
            return (
              <Table.Row key={transfer.id}>
                <Table.Cell>
                  <AffiliateInfo
                    name={affiliate?.name || t('payouts.transfers.unknown')}
                    avatarUrl={affiliate?.avatar?.fileUrl ?? undefined}
                    programName={transfer.programName || t('payouts.transfers.unknown')}
                    size="xs"
                  />
                </Table.Cell>
                <Table.Cell>
                  <Money
                    value={transfer.amount.amount}
                    currency={transfer.amount.currencyCode}
                    fontSize="sm"
                  />
                </Table.Cell>
                <Table.Cell>
                  <TransferStatusCell transfer={transfer} />
                </Table.Cell>
                <Table.Cell textAlign="end">
                  {/* A statement exists only once the money actually reached the
                      affiliate, so the column stays empty on every other row. */}
                  {transfer.status === PayoutTransferStatus.SUCCEEDED && (
                    <Tooltip content={t('payouts.statement.download')} showArrow>
                      <IconButton
                        aria-label={t('payouts.statement.download')}
                        variant="ghost"
                        size="sm"
                        loading={downloadingTransferId === transfer.id}
                        onClick={() =>
                          downloadStatement({ batchId: payoutId, transferId: transfer.id })
                        }
                      >
                        <Download width={16} height={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
      {/* Show more button and fade effect */}
      {transfers.length > MAX_TRANSFERS_DISPLAY && !showAll && (
        <Box
          rounded="lg"
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          h="60px"
          pointerEvents="none"
          overflow="hidden"
        >
          <Box
            position="absolute"
            left={0}
            right={0}
            bottom={0}
            h="60px"
            bgGradient="to-b"
            gradientFrom="transparent"
            gradientTo="white"
            zIndex={1}
            pointerEvents="none"
          />
          <Flex
            justify="center"
            align="center"
            position="absolute"
            left={0}
            right={0}
            bottom={2}
            zIndex={2}
            pointerEvents="auto"
          >
            <Button size="sm" variant="outline" bg="white" onClick={() => setShowAll(true)}>
              {t('payouts.transfers.table.showMore')}
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default PayoutTransfersTable;
